<template>
  <section id="team" class="team-showcase-section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">团队风采</h2>
        <div class="title-underline"></div>
        <p class="section-description">
          我们拥有一支由资深医疗专家、研究人员和技术人员组成的多元化团队，致力于推动医疗创新和患者福祉。
        </p>
      </div>

      <div class="team-grid">
        <!-- 团队成员卡片 -->
        <div 
          v-for="(member, index) in teamMembers" 
          :key="index"
          class="team-member-card"
          v-motion
          :initial="{ opacity: 0, y: 50 }"
          :visible="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.8, delay: index * 0.1 }"
        >
          <div class="member-image-container">
            <img 
              :src="member.image" 
              :alt="member.name"
              class="member-image"
            >
          </div>
          <div class="member-info">
            <h3 class="member-name">{{ member.name }}</h3>
            <p class="member-position">{{ member.position }}</p>
            <p class="member-bio">{{ member.bio }}</p>
            <div class="member-social">
              <a v-if="member.linkedin" :href="member.linkedin" class="social-link" target="_blank">
                <i class="fab fa-linkedin-in"></i>
              </a>
              <a v-if="member.twitter" :href="member.twitter" class="social-link" target="_blank">
                <i class="fab fa-twitter"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'

const teamMembers = ref([
  {
    name: "张伟博士",
    position: "首席执行官",
    bio: "拥有20年临床研究经验，曾领导多个国际多中心临床试验项目。",
    image:  require("@/assets/doctor_one.png")
  },
  {
    name: "李娜博士",
    position: "首席医疗官",
    bio: "专注于肿瘤学研究，发表过100多篇SCI论文，拥有多项专利。",
    image: require("@/assets/doctor_two.png")
  },
  {
    name: "王强博士",
    position: "首席技术官",
    bio: "医疗信息系统专家，主导开发了多个医疗数据管理平台。",
    image: require("@/assets/doctor_three.png")
  },
  {
    name: "陈丽博士",
    position: "临床研究总监",
    bio: "资深临床项目经理，管理过50多个I-III期临床试验。",
    image: require("@/assets/doctor_four.png")
  }
])
</script>

<style scoped>
.team-showcase-section {
  padding: 3rem 0; /* 减小移动设备的上下内边距 */
  background-color: #f9fafb;
}

.container {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  box-sizing: border-box; /* 确保padding不会增加总宽度 */
}

/* 响应式容器调整 */
@media (min-width: 640px) {
  .container {
    padding: 0 1.5rem;
  }
}

@media (min-width: 1024px) {
  .container {
    padding: 0 2rem;
  }
}

.section-header {
  text-align: center;
  margin-bottom: 2rem; /* 移动设备减小间距 */
  padding: 0 0.5rem; /* 增加左右内边距防止内容贴边 */
}

.section-title {
  font-size: 1.75rem; /* 移动设备减小标题大小 */
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;
  line-height: 1.2; /* 优化行高 */
}

@media (min-width: 768px) {
  .section-title {
    font-size: 2.25rem; /* 平板及以上设备增大标题 */
    margin-bottom: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .section-title {
    font-size: 3rem; /* 桌面设备进一步增大标题 */
  }
}

.title-underline {
  width: 4rem; /* 移动设备减小下划线长度 */
  height: 0.25rem;
  background-color: #2563eb;
  margin: 0 auto 1rem;
  border-radius: 9999px;
}

@media (min-width: 768px) {
  .title-underline {
    width: 5rem; /* 平板及以上增大下划线长度 */
  }
}

.section-description {
  color: #374151;
  font-size: 1rem; /* 移动设备减小描述文字 */
  max-width: 100%; /* 移动设备使用全宽 */
  margin: 0 auto;
  line-height: 1.6; /* 优化行高提高可读性 */
  padding: 0 1rem; /* 增加内边距 */
}

@media (min-width: 768px) {
  .section-description {
    max-width: 36rem; /* 平板及以上限制最大宽度 */
    font-size: 1.125rem;
  }
}

/* 团队网格布局 - 移动优先 */
.team-grid {
  display: grid;
  grid-template-columns: 1fr; /* 默认单列布局，适合手机 */
  gap: 2rem; /* 减小间距 */
  margin-bottom: 2rem;
  padding: 0 0.5rem;
}

/* 平板设备：2列布局 */
@media (min-width: 640px) {
  .team-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }
}

/* 桌面设备：4列布局 */
@media (min-width: 1024px) {
  .team-grid {
    grid-template-columns: repeat(4, 1fr);
    margin-bottom: 4rem;
  }
}

.team-member-card {
  background-color: white;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); /* 移动设备减小阴影 */
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.team-member-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.member-image-container {
  height: 240px; /* 移动设备减小图片高度 */
  overflow: hidden;
}

@media (min-width: 640px) {
  .member-image-container {
    height: 280px; /* 平板增大图片高度 */
  }
}

@media (min-width: 1024px) {
  .member-image-container {
    height: 300px; /* 桌面设备恢复原高度 */
  }
}

.member-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.team-member-card:hover .member-image {
  transform: scale(1.05); /* 悬停时图片轻微放大 */
}

.member-info {
  padding: 1.25rem; /* 移动设备减小内边距 */
  flex: 1;
  display: flex;
  flex-direction: column;
}

@media (min-width: 640px) {
  .member-info {
    padding: 1.5rem; /* 平板恢复内边距 */
  }
}

.member-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
}

.member-position {
  color: #2563eb;
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
}

.member-bio {
  color: #374151;
  margin-bottom: 1.25rem;
  line-height: 1.6;
  font-size: 0.95rem;
  flex: 1;
}

.member-social {
  display: flex;
  gap: 0.75rem; /* 减小社交图标间距 */
  margin-top: auto;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem; /* 减小社交图标大小 */
  height: 2.25rem;
  border-radius: 50%;
  background-color: #e5e7eb;
  color: #374151;
  transition: all 0.3s ease;
}

.social-link:hover {
  background-color: #2563eb;
  color: white;
  transform: scale(1.1);
}

/* 针对超小屏幕的额外优化 */
@media (max-width: 360px) {
  .team-showcase-section {
    padding: 2rem 0;
  }
  
  .section-title {
    font-size: 1.5rem;
  }
  
  .member-image-container {
    height: 220px;
  }
}
</style>
